import { useConversationList } from "@/api/services/admin/conversation";
import { TGetConversationDto } from "@/api/services/admin/conversation/dtos/get-conversation.dto";
import { TGetMessageOutputDto } from "@/api/services/admin/conversation/dtos/get-message-list-by-conversation.dto";
import { CHANNELS, EVENTS } from "@/constants/socket";
import { useSocket } from "@/providers/SocketProvider";
import { isFireEvent } from "@/utils/pieces";
import { useEffect, useState } from "react";

export const useRoomList = () => {
  const { socket } = useSocket();
  const { dataFormatter, hasNextPage, isFetching, fetchNextPage, refetch: refetchConversationList } = useConversationList();
  const [oldRooms, setOldRooms] = useState<TGetConversationDto[]>([]);
  const [newRooms, setNewRooms] = useState<TGetConversationDto[]>([]);

  const changeLastMessage = (receivedMessage: TGetMessageOutputDto) => {
    setOldRooms((prev) => {
      const found = prev.find((room) => room.id.toString() === receivedMessage.conversationId.toString());
      if (!found) return prev;
      found.messages = [receivedMessage];
      return prev;
    });
  };

  useEffect(() => {
    if (!isFetching) {
      setOldRooms(dataFormatter);
    }
  }, [isFetching]);

  useEffect(() => {
    if (socket) {
      socket.emit(EVENTS.CONVERSATION.JOIN_ROOM_LIST, CHANNELS.CONVERSATION.ROOM_LIST);
      socket.on(EVENTS.CONVERSATION.NEW_ROOM, (room) => {
        setNewRooms((cur) => [...cur, room]);
      });
      socket.on(EVENTS.CONVERSATION.FETCH_LIST, () => {
        refetchConversationList();
      });
      return () => {
        socket.emit(EVENTS.CONVERSATION.LEAVE, CHANNELS.CONVERSATION.ROOM_LIST);
        socket.off(EVENTS.CONVERSATION.NEW_ROOM);
        socket.off(EVENTS.CONVERSATION.FETCH_LIST);
      };
    }
  }, [socket]);

  useEffect(() => {
    const callback = (e: Event) => {
      if (isFireEvent<TGetMessageOutputDto>(e)) {
        const { detail: receivedMessage } = e;
        changeLastMessage(receivedMessage);
        setOldRooms((prevRooms) => {
          const roomIndex = prevRooms.findIndex((room) => receivedMessage.conversationId.toString() === room.id.toString());
          if (roomIndex === -1) return prevRooms;
          const updatedRoom = { ...prevRooms[roomIndex], subtitle: receivedMessage.text };
          const updatedRooms = [updatedRoom, ...prevRooms.filter((_, index) => index !== roomIndex)];
          return updatedRooms;
        });
      }
    };
    window.addEventListener(EVENTS.CONVERSATION.NEW_MESSAGE, callback);
    return () => {
      window.removeEventListener(EVENTS.CONVERSATION.NEW_MESSAGE, callback);
    };
  }, []);

  return {
    oldRooms: oldRooms,
    newRooms,
    hasNextPage,
    isFetching,
    fetchNextPage,
  };
};
